/**
 * Servicio para gestionar actividades creadas por profesores
 * Proyecto: Alianza Francesa / Galia - Sistema de Despliegue de Actividades
 */

import { supabaseService } from './supabaseService';
import { TeacherActivity, ActivityObjective } from '../types';

class TeacherActivityService {
  /**
   * Crear nueva actividad (borrador)
   */
  async createActivity(
    teacherEmail: string,
    taskDescription: string,
    objectives: ActivityObjective[],
    classroomId?: number | null,
    level?: string | null
  ): Promise<TeacherActivity | null> {
    try {
      // Asegurar que classroom_id sea un número entero o null
      const classroomIdNumber = classroomId ? (typeof classroomId === 'number' ? classroomId : parseInt(String(classroomId), 10)) : null;
      
      // Validar que classroom_id sea un número válido si se proporciona
      if (classroomIdNumber !== null && (isNaN(classroomIdNumber) || classroomIdNumber <= 0)) {
        throw new Error(`classroom_id inválido: ${classroomId}. Debe ser un número entero positivo.`);
      }

      // Generar UUID único para tache_id (requerido por la base de datos)
      const tacheId = crypto.randomUUID();

      const insertData = {
        teacher_email: teacherEmail,
        task_description: taskDescription,
        objectives: objectives,
        classroom_id: classroomIdNumber,
        level: level || null,
        is_deployed: false,
        tache_id: tacheId,
      };

      console.log('[TeacherActivityService] 📝 Intentando crear actividad con datos:', {
        teacher_email: insertData.teacher_email,
        task_description_length: insertData.task_description.length,
        objectives_count: insertData.objectives.length,
        objectives: insertData.objectives,
        classroom_id: insertData.classroom_id,
        level: insertData.level,
        is_deployed: insertData.is_deployed
      });

      const { data, error } = await supabaseService
        .getClient()
        .from('teacher_activities')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('[TeacherActivityService] ❌ Error de Supabase al crear actividad:', {
          error,
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      console.log('[TeacherActivityService] ✅ Actividad creada exitosamente:', data);
      return this.mapToTeacherActivity(data);
    } catch (error: any) {
      console.error('[TeacherActivityService] ❌ Error al crear actividad:', error);
      console.error('[TeacherActivityService] 📋 Stack trace:', error?.stack);
      throw error; // Re-lanzar el error para que el formulario pueda manejarlo
    }
  }

  /**
   * Actualizar actividad existente
   */
  async updateActivity(
    activityId: string,
    updates: {
      task_description?: string;
      objectives?: ActivityObjective[];
      classroom_id?: number | null;
      level?: string | null;
    }
  ): Promise<TeacherActivity | null> {
    try {
      const updateData: any = {};
      if (updates.task_description !== undefined) {
        updateData.task_description = updates.task_description;
      }
      if (updates.objectives !== undefined) {
        updateData.objectives = updates.objectives;
      }
      if (updates.classroom_id !== undefined) {
        updateData.classroom_id = updates.classroom_id;
      }
      if (updates.level !== undefined) {
        updateData.level = updates.level;
      }

      const { data, error } = await supabaseService
        .getClient()
        .from('teacher_activities')
        .update(updateData)
        .eq('id', activityId)
        .select()
        .single();

      if (error) {
        console.error('[TeacherActivityService] Error al actualizar actividad:', error);
        throw error;
      }

      return this.mapToTeacherActivity(data);
    } catch (error) {
      console.error('[TeacherActivityService] Error al actualizar actividad:', error);
      return null;
    }
  }

  /**
   * Desplegar actividad: marcar como desplegada y generar tache_id
   */
  async deployActivity(activityId: string): Promise<TeacherActivity | null> {
    try {
      // Generar UUID único para tache_id
      const tacheId = crypto.randomUUID();

      const { data, error } = await supabaseService
        .getClient()
        .from('teacher_activities')
        .update({
          is_deployed: true,
          tache_id: tacheId,
        })
        .eq('id', activityId)
        .select()
        .single();

      if (error) {
        console.error('[TeacherActivityService] Error al desplegar actividad:', error);
        throw error;
      }

      return this.mapToTeacherActivity(data);
    } catch (error) {
      console.error('[TeacherActivityService] Error al desplegar actividad:', error);
      return null;
    }
  }

  /**
   * Obtener actividad por tache_id (para URLs)
   */
  async getActivityByTacheId(tacheId: string): Promise<TeacherActivity | null> {
    try {
      const { data, error } = await supabaseService
        .getClient()
        .from('teacher_activities')
        .select('*')
        .eq('tache_id', tacheId)
        .eq('is_deployed', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No se encontró la actividad
          return null;
        }
        console.error('[TeacherActivityService] Error al obtener actividad por tache_id:', error);
        throw error;
      }

      return this.mapToTeacherActivity(data);
    } catch (error) {
      console.error('[TeacherActivityService] Error al obtener actividad por tache_id:', error);
      return null;
    }
  }

  /**
   * Obtener actividad por ID
   */
  async getActivityById(activityId: string): Promise<TeacherActivity | null> {
    try {
      const { data, error } = await supabaseService
        .getClient()
        .from('teacher_activities')
        .select('*')
        .eq('id', activityId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('[TeacherActivityService] Error al obtener actividad por ID:', error);
        throw error;
      }

      return this.mapToTeacherActivity(data);
    } catch (error) {
      console.error('[TeacherActivityService] Error al obtener actividad por ID:', error);
      return null;
    }
  }

  /**
   * Listar todas las actividades de un profesor
   */
  async getTeacherActivities(teacherEmail: string): Promise<TeacherActivity[]> {
    try {
      const { data, error } = await supabaseService
        .getClient()
        .from('teacher_activities')
        .select('*')
        .eq('teacher_email', teacherEmail)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[TeacherActivityService] Error al obtener actividades del profesor:', error);
        throw error;
      }

      return (data || []).map(item => this.mapToTeacherActivity(item));
    } catch (error) {
      console.error('[TeacherActivityService] Error al obtener actividades del profesor:', error);
      return [];
    }
  }

  /**
   * Listar actividades disponibles para un estudiante
   * Filtra por: actividades desplegadas Y (classroom_id NULL O classroom_id en la lista del estudiante)
   */
  async getStudentActivities(studentClassroomIds: number[]): Promise<TeacherActivity[]> {
    try {
      // Console log para verificar los classroom_ids recibidos
      console.log('[TeacherActivityService] 🔍 getStudentActivities llamado con:', {
        studentClassroomIds: studentClassroomIds,
        totalIds: studentClassroomIds.length
      });
      
      // Construir consulta: actividades desplegadas Y (classroom_id IS NULL O classroom_id IN lista)
      let query = supabaseService
        .getClient()
        .from('teacher_activities')
        .select('*')
        .eq('is_deployed', true);

      // Si el estudiante tiene aulas, incluir actividades generales (NULL) y de sus aulas
      if (studentClassroomIds.length > 0) {
        // Intentar con sintaxis alternativa de Supabase para OR
        // Opción 1: Sintaxis estándar de PostgREST
        const orCondition = `classroom_id.is.null,classroom_id.in.(${studentClassroomIds.join(',')})`;
        console.log('[TeacherActivityService] 📋 Consulta SQL construida (intento 1):', {
          condition: orCondition,
          classroomIds: studentClassroomIds,
          classroomIdsType: studentClassroomIds.map(id => typeof id)
        });
        
        try {
          query = query.or(orCondition);
        } catch (orError) {
          console.error('[TeacherActivityService] ❌ Error con sintaxis OR estándar, intentando alternativa:', orError);
          // Opción 2: Hacer dos consultas separadas y combinarlas
          // Por ahora, intentar con sintaxis alternativa
          const altCondition = `classroom_id.is.null,classroom_id.eq.${studentClassroomIds[0]}`;
          if (studentClassroomIds.length > 1) {
            // Si hay más de un ID, necesitamos múltiples condiciones OR
            const conditions = [
              'classroom_id.is.null',
              ...studentClassroomIds.map(id => `classroom_id.eq.${id}`)
            ];
            query = query.or(conditions.join(','));
          } else {
            query = query.or(altCondition);
          }
        }
      } else {
        // Si no tiene aulas, solo actividades generales
        console.log('[TeacherActivityService] 📋 Consulta SQL: solo actividades generales (classroom_id IS NULL)');
        query = query.is('classroom_id', null);
      }

      // Log de la consulta antes de ejecutarla
      console.log('[TeacherActivityService] 🔍 Ejecutando consulta a Supabase...');
      
      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('[TeacherActivityService] ❌ Error al obtener actividades del estudiante:', {
          error: error,
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      console.log('[TeacherActivityService] ✅ Respuesta de Supabase:', {
        hasData: !!data,
        dataType: Array.isArray(data) ? 'array' : typeof data,
        total: data?.length || 0,
        rawData: data
      });

      console.log('[TeacherActivityService] ✅ Actividades obtenidas de Supabase:', {
        total: data?.length || 0,
        activities: data?.map((item: any) => ({
          id: item.id,
          classroom_id: item.classroom_id,
          is_deployed: item.is_deployed,
          task_description: item.task_description?.substring(0, 50)
        }))
      });

      // Si no hay resultados, hacer consultas de debug para diagnosticar el problema
      if (!data || data.length === 0) {
        console.log('[TeacherActivityService] 🔍 No se encontraron actividades, haciendo consultas de debug...');
        
        // Debug 1: Ver todas las actividades (sin filtros)
        const debugQuery1 = supabaseService
          .getClient()
          .from('teacher_activities')
          .select('id, classroom_id, is_deployed, task_description')
          .limit(10);
        
        const { data: debugData1, error: debugError1 } = await debugQuery1;
        
        if (debugError1) {
          console.error('[TeacherActivityService] ❌ Error en consulta de debug 1:', debugError1);
        } else {
          console.log('[TeacherActivityService] 🔍 Debug 1 - Todas las actividades en la tabla:', {
            total: debugData1?.length || 0,
            activities: debugData1
          });
        }
        
        // Debug 2: Ver actividades desplegadas
        const debugQuery2 = supabaseService
          .getClient()
          .from('teacher_activities')
          .select('id, classroom_id, is_deployed, task_description')
          .eq('is_deployed', true)
          .limit(10);
        
        const { data: debugData2, error: debugError2 } = await debugQuery2;
        
        if (debugError2) {
          console.error('[TeacherActivityService] ❌ Error en consulta de debug 2:', debugError2);
        } else {
          console.log('[TeacherActivityService] 🔍 Debug 2 - Actividades desplegadas (is_deployed=true):', {
            total: debugData2?.length || 0,
            activities: debugData2
          });
        }
        
        // Debug 3: Ver actividades con el classroom_id específico
        if (studentClassroomIds.length > 0) {
          const debugQuery3 = supabaseService
            .getClient()
            .from('teacher_activities')
            .select('id, classroom_id, is_deployed, task_description')
            .eq('classroom_id', studentClassroomIds[0])
            .limit(10);
          
          const { data: debugData3, error: debugError3 } = await debugQuery3;
          
          if (debugError3) {
            console.error('[TeacherActivityService] ❌ Error en consulta de debug 3:', debugError3);
          } else {
            console.log('[TeacherActivityService] 🔍 Debug 3 - Actividades con classroom_id específico:', {
              classroomId: studentClassroomIds[0],
              total: debugData3?.length || 0,
              activities: debugData3
            });
          }
        }
      }

      return (data || []).map(item => this.mapToTeacherActivity(item));
    } catch (error) {
      console.error('[TeacherActivityService] Error al obtener actividades del estudiante:', error);
      return [];
    }
  }

  /**
   * Eliminar actividad (borradores o desplegadas)
   */
  async deleteActivity(activityId: string, teacherEmail: string): Promise<boolean> {
    try {
      const activity = await this.getActivityById(activityId);
      if (!activity) {
        console.error('[TeacherActivityService] Actividad no encontrada');
        return false;
      }

      if (activity.teacher_email !== teacherEmail) {
        console.error('[TeacherActivityService] La actividad no pertenece al profesor');
        return false;
      }

      const { error } = await supabaseService
        .getClient()
        .from('teacher_activities')
        .delete()
        .eq('id', activityId)
        .eq('teacher_email', teacherEmail);

      if (error) {
        console.error('[TeacherActivityService] Error al eliminar actividad:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('[TeacherActivityService] Error al eliminar actividad:', error);
      return false;
    }
  }

  /**
   * Mapear datos de Supabase a TeacherActivity
   */
  private mapToTeacherActivity(data: any): TeacherActivity {
    return {
      id: data.id,
      tache_id: data.tache_id,
      teacher_email: data.teacher_email,
      classroom_id: data.classroom_id,
      task_description: data.task_description,
      objectives: (data.objectives || []) as ActivityObjective[],
      level: data.level,
      is_deployed: data.is_deployed || false,
      created_at: data.created_at,
      updated_at: data.updated_at,
    };
  }

  /**
   * Generar URL para compartir actividad
   */
  generateActivityUrl(tacheId: string, baseUrl?: string): string {
    const base = baseUrl || window.location.origin;
    return `${base}?tache_id=${tacheId}`;
  }
}

export const teacherActivityService = new TeacherActivityService();
